import * as React from "react";
import { forwardRef, useCallback } from "react";
import { styled } from "@mui/material";
import { SymptomText } from "../uiControllers/SymptomText";
import { QualifiedFeature } from "fe-common/graphql";
import { QuestionDescription } from "./questionDescription";
import clsx from "clsx";
import { ShowInChatReasoningButton } from "../demodebug/mobileSuggestionZoom/showInChatReasoningButton";
import { AnswerChip } from "../../style/chips";

//region [[ Styles ]]

const MultipleQuestionOptionChipView = styled("div")({
  display: "flex",
  width: "100%",
  position: "relative",
  marginBottom: "15px",
});

const QuestionChip = styled("div")({
  display: "flex",
  flexFlow: "row",
  width: "100%",
  flexWrap: "wrap",
  alignItems: "center",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface MultipleQuestionOptionChipProps {
  className?: string;
  onClick: (qualifiedFeature: QualifiedFeature) => void;
  selected?: boolean;
  disabled?: boolean;
  labeler?: (qualifiedFeature: QualifiedFeature) => React.ReactNode;
  qualifiedFeature: QualifiedFeature;
  questionText: string;
  /** Hide description even if its available?, default is false **/
  hideDescription?: boolean;
  // Whether we can add a "Whats the reasoning" button in demo mode
  basedOnReasoning: boolean;
  // Is this a background questions? we need this to be able to ask reasoning
  // for that questions properly (see: showInChatReasoningButton.tsx for more info)
  isBackground: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MultipleQuestionOptionChip = forwardRef<
  HTMLDivElement,
  MultipleQuestionOptionChipProps
>(
  (
    { qualifiedFeature, onClick, questionText, basedOnReasoning, ...props },
    ref,
  ) => {
    const handleClicked = useCallback(() => {
      onClick(qualifiedFeature);
    }, [onClick, qualifiedFeature]);

    return (
      <MultipleQuestionOptionChipView
        className={clsx(props.className, "multiple-question-option-chip")}
        ref={ref}
      >
        <QuestionChip className={"question-chip"}>
          <AnswerChip
            className={"answer-chip"}
            label={
              props?.labeler?.(qualifiedFeature) || (
                <SymptomText symptom={qualifiedFeature} />
              )
            }
            onClick={handleClicked}
            data-selected={props.selected}
            disabled={props.disabled}
          />
          {!props.hideDescription && (
            <QuestionDescription
              qualifiedFeature={qualifiedFeature.feature}
              featureId={qualifiedFeature.id}
              questionText={questionText}
            />
          )}
          {basedOnReasoning && (
            <ShowInChatReasoningButton
              suggestion={qualifiedFeature}
              isBackgroundSuggestion={props.isBackground}
            />
          )}
        </QuestionChip>
      </MultipleQuestionOptionChipView>
    );
  },
);
